/**
 * Feedback capture (M2-DESIGN §6).
 *
 * The player writes a category, a rating and a sentence; everything else is collected
 * here so a bug report arrives with the numbers that explain it — build, backend, GPU,
 * frame times over the last minute, rtt, where they were standing and what just happened.
 */

import { DEFAULT_MAP_ID } from '@stillsun/shared';
import { ApiError, api } from '../net/api.js';
import { base64Of, type FrameCapture } from '../render/capture.js';
import { gfxStats } from '../render/renderer.js';

export const FEEDBACK_CATEGORIES = ['bug', 'balance', 'idea', 'performance', 'other'] as const;
export type FeedbackCategory = (typeof FEEDBACK_CATEGORIES)[number] | 'pulse';

export const MAX_FEEDBACK_TEXT = 1000;
/** Frame samples are taken at most this often; 60 s of them is the reported window. */
const SAMPLE_INTERVAL_MS = 100;
const WINDOW_MS = 60_000;
const MAX_EVENTS = 20;

export interface FeedbackContext {
  build: string;
  backend: string;
  gpu: string;
  quality: string;
  fpsP50: number;
  fpsP1: number;
  rtt: number;
  mapId: string;
  mode: string;
  phase: string;
  pos: { x: number; y: number; z: number };
  events: string[];
  screen: string;
  ua: string;
  /** Resolved tier, backing store and DRS scale, and main-thread ms per frame (see GfxStats). */
  tier: string;
  res: string;
  cpuMs: number;
  /** 'on (2.1 ms)' or 'off (43 ms)': the boot JIT probe (main.ts) — Edge's Enhanced Security Mode. */
  jit: string;
}

export interface FeedbackPayload {
  category: FeedbackCategory;
  rating: number;
  text: string;
  context: FeedbackContext;
  /** Base64 JPEG without the data-URL prefix. */
  screenshot?: string;
}

interface Sample {
  t: number;
  fps: number;
}

const buildHash = (): string => {
  const v: unknown = import.meta.env.VITE_BUILD;
  return typeof v === 'string' && v.length > 0 ? v : 'dev';
};

/**
 * GPU string. WebGPU exposes `adapter.info` (Chrome 128+); the WebGL2 path has the old
 * `WEBGL_debug_renderer_info` extension, which several browsers now stub out. Either may
 * be unavailable, so this resolves to 'unknown' rather than failing.
 */
async function probeGpu(): Promise<string> {
  try {
    const gpu = (navigator as Navigator & { gpu?: { requestAdapter(): Promise<unknown> } }).gpu;
    if (gpu) {
      const adapter = (await gpu.requestAdapter()) as {
        info?: { vendor?: string; architecture?: string; device?: string; description?: string };
      } | null;
      const info = adapter?.info;
      if (info) {
        const parts = [info.vendor, info.architecture, info.device, info.description]
          .filter((s): s is string => typeof s === 'string' && s.length > 0)
          .join(' ');
        if (parts.length > 0) return parts.slice(0, 160);
      }
    }
  } catch {
    /* fall through to WebGL */
  }
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl2') ?? canvas.getContext('webgl');
    if (gl) {
      const ext = gl.getExtension('WEBGL_debug_renderer_info') as {
        UNMASKED_RENDERER_WEBGL: number;
      } | null;
      if (ext) {
        const name: unknown = gl.getParameter(ext.UNMASKED_RENDERER_WEBGL);
        if (typeof name === 'string' && name.length > 0) return name.slice(0, 160);
      }
      const fallback: unknown = gl.getParameter(gl.RENDERER);
      if (typeof fallback === 'string' && fallback.length > 0) return fallback.slice(0, 160);
    }
  } catch {
    /* no WebGL either */
  }
  return 'unknown';
}

export class FeedbackCollector {
  private readonly samples: Sample[] = [];
  private readonly events: string[] = [];
  private lastSampleAt = 0;
  private gpu = 'probing…';

  /** Everything the frame loop knows and the form does not. */
  live = {
    backend: 'unknown',
    quality: 'auto',
    rtt: 0,
    mapId: DEFAULT_MAP_ID,
    mode: 'expedition',
    phase: 'none',
    pos: { x: 0, y: 0, z: 0 },
    jit: 'unknown',
  };

  constructor() {
    void probeGpu().then((s) => {
      this.gpu = s;
    });
  }

  /** Call once per frame with the frame delta in seconds. */
  sampleFrame(dtSeconds: number, nowMs: number): void {
    if (dtSeconds <= 0) return;
    if (nowMs - this.lastSampleAt < SAMPLE_INTERVAL_MS) return;
    this.lastSampleAt = nowMs;
    this.samples.push({ t: nowMs, fps: 1 / dtSeconds });
    // Drop anything older than the reporting window.
    const cutoff = nowMs - WINDOW_MS;
    let drop = 0;
    while (drop < this.samples.length) {
      const s = this.samples[drop];
      if (!s || s.t >= cutoff) break;
      drop += 1;
    }
    if (drop > 0) this.samples.splice(0, drop);
  }

  /** A short human line: 'killed Chorister', 'opened container 3', 'extracted'. */
  logEvent(text: string): void {
    const stamp = new Date().toISOString().slice(11, 19);
    this.events.push(`${stamp} ${text}`);
    while (this.events.length > MAX_EVENTS) this.events.shift();
  }

  get recentEvents(): readonly string[] {
    return this.events;
  }

  /** p50 and p1 (the 1st percentile — the bad frames) of fps over the window. */
  percentiles(): { p50: number; p1: number } {
    if (this.samples.length === 0) return { p50: 0, p1: 0 };
    const sorted = this.samples.map((s) => s.fps).sort((a, b) => a - b);
    const at = (frac: number): number => {
      const idx = Math.min(sorted.length - 1, Math.max(0, Math.round(frac * (sorted.length - 1))));
      return Math.round(sorted[idx] ?? 0);
    };
    return { p50: at(0.5), p1: at(0.01) };
  }

  snapshot(): FeedbackContext {
    const { p50, p1 } = this.percentiles();
    return {
      build: buildHash(),
      backend: this.live.backend,
      gpu: this.gpu,
      quality: this.live.quality,
      fpsP50: p50,
      fpsP1: p1,
      rtt: Math.round(this.live.rtt),
      mapId: this.live.mapId,
      mode: this.live.mode,
      phase: this.live.phase,
      pos: {
        x: Math.round(this.live.pos.x * 10) / 10,
        y: Math.round(this.live.pos.y * 10) / 10,
        z: Math.round(this.live.pos.z * 10) / 10,
      },
      events: [...this.events],
      screen: `${String(window.innerWidth)}x${String(window.innerHeight)}@${String(window.devicePixelRatio || 1)}`,
      ua: navigator.userAgent.slice(0, 200),
      tier: gfxStats.tier,
      res: `${String(gfxStats.drawW)}x${String(gfxStats.drawH)}@${gfxStats.scale.toFixed(2)}`,
      cpuMs: Math.round(gfxStats.cpuMs * 10) / 10,
      jit: this.live.jit,
    };
  }
}

export interface SubmitResult {
  ok: boolean;
  /** '' on success, otherwise something to show the player. */
  message: string;
}

/**
 * POSTs the report. A missing endpoint is not the player's problem: their words are kept
 * in localStorage so a later build can flush them, and the UI still says thank you.
 */
export async function submitFeedback(
  payload: FeedbackPayload,
  token: string,
): Promise<SubmitResult> {
  const body: FeedbackPayload = {
    ...payload,
    text: payload.text.slice(0, MAX_FEEDBACK_TEXT),
    rating: Math.max(1, Math.min(5, Math.round(payload.rating))),
  };
  try {
    await api('/api/feedback', { body, token });
    return { ok: true, message: '' };
  } catch (err) {
    queueOffline(body);
    if (err instanceof ApiError && err.missing) {
      return { ok: true, message: 'saved locally — the terminal is offline' };
    }
    return { ok: false, message: err instanceof Error ? err.message : 'send failed' };
  }
}

const QUEUE_KEY = 'stillsun.feedback.queue';
const MAX_QUEUED = 10;

function queueOffline(payload: FeedbackPayload): void {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    const list: unknown = raw === null ? [] : JSON.parse(raw);
    const arr = Array.isArray(list) ? list : [];
    // Screenshots are megabytes of base64; never queue them.
    const { screenshot: _drop, ...rest } = payload;
    void _drop;
    arr.push({ ...rest, queuedAt: Date.now() });
    while (arr.length > MAX_QUEUED) arr.shift();
    localStorage.setItem(QUEUE_KEY, JSON.stringify(arr));
  } catch {
    /* quota or private mode — the report is lost, which is acceptable */
  }
}

/** Best-effort flush of anything queued while the endpoint was down. */
export async function flushQueuedFeedback(token: string): Promise<number> {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(QUEUE_KEY);
  } catch {
    return 0;
  }
  if (raw === null) return 0;
  let list: unknown;
  try {
    list = JSON.parse(raw);
  } catch {
    return 0;
  }
  if (!Array.isArray(list) || list.length === 0) return 0;
  let sent = 0;
  const left: unknown[] = [];
  for (const entry of list) {
    try {
      await api('/api/feedback', { body: entry, token });
      sent += 1;
    } catch {
      left.push(entry);
    }
  }
  try {
    if (left.length === 0) localStorage.removeItem(QUEUE_KEY);
    else localStorage.setItem(QUEUE_KEY, JSON.stringify(left));
  } catch {
    /* ignore */
  }
  return sent;
}

/** Grabs a downscaled JPEG of the next rendered frame, as raw base64. */
export async function captureScreenshot(capture: FrameCapture): Promise<string | null> {
  const url = await capture.request({ maxWidth: 1280, type: 'image/jpeg', quality: 0.7 });
  return url === null ? null : base64Of(url);
}
